export type QuestionType = 'self-efficacy' | 'interest-enjoyment' | 'emotion';

export interface SurveyQuestion {
  id: string;
  type: QuestionType;
  questionText: string;
  isReverseScored?: boolean;
}

export const SELF_EFFICACY_QUESTIONS: SurveyQuestion[] = [
  {
    id: 'self_efficacy_1',
    type: 'self-efficacy',
    questionText: "I'm confident that I can do an excellent job on my math tests",
  },
  {
    id: 'self_efficacy_2',
    type: 'self-efficacy',
    questionText: "I'm confident I can get even the hardest material presented by my math teacher",
  },
  {
    id: 'self_efficacy_3',
    type: 'self-efficacy',
    questionText: "I'm confident I can do an excellent job on my math assignments",
  },
  {
    id: 'self_efficacy_4',
    type: 'self-efficacy',
    questionText: "I'm certain I can master the skills being taught in this class",
  },
  {
    id: 'self_efficacy_5',
    type: 'self-efficacy',
    questionText: 'Even if the work is hard, I can learn it',
  },
  {
    id: 'self_efficacy_6',
    type: 'self-efficacy',
    questionText: 'Even if I make a mistake, I am sure I can figure it out',
  },
  {
    id: 'self_efficacy_7',
    type: 'self-efficacy',
    questionText: 'I do not feel sure about myself in problem-solving',
  },
  {
    id: 'self_efficacy_8',
    type: 'self-efficacy',
    questionText: 'I could try my hardest and still not get math',
    isReverseScored: true,
  },
  {
    id: 'self_efficacy_9',
    type: 'self-efficacy',
    questionText: 'Even when I study very hard, I do poorly in math',
    isReverseScored: true,
  },
];

export const INTEREST_ENJOYMENT_QUESTIONS: SurveyQuestion[] = [
  {
    id: 'interest_1',
    type: 'interest-enjoyment',
    questionText: 'I enjoyed doing this activity very much',
  },
  {
    id: 'interest_2',
    type: 'interest-enjoyment',
    questionText: 'This activity was fun to do',
  },
  {
    id: 'interest_3',
    type: 'interest-enjoyment',
    questionText: 'I thought this was a boring activity',
  },
  {
    id: 'interest_4',
    type: 'interest-enjoyment',
    questionText: 'This activity did not hold my attention at all',
  },
  {
    id: 'interest_5',
    type: 'interest-enjoyment',
    questionText: 'I would describe this activity as very interesting',
  },
  {
    id: 'interest_6',
    type: 'interest-enjoyment',
    questionText: 'I thought this activity was quite enjoyable',
  },
  {
    id: 'interest_7',
    type: 'interest-enjoyment',
    questionText: 'While I was doing this activity, I was thinking about how much I enjoyed it',
  },
  {
    id: 'interest_8',
    type: 'interest-enjoyment',
    questionText: 'I believe this activity could be of some value to me',
  },
  {
    id: 'interest_9',
    type: 'interest-enjoyment',
    questionText: 'I think that doing this activity is useful for my learning',
  },
  {
    id: 'interest_10',
    type: 'interest-enjoyment',
    questionText: 'I think this is important to do because it can help me improve',
  },
];

const EMOTION_QUESTION: SurveyQuestion = {
  id: 'current_emotion',
  type: 'emotion',
  questionText: 'How are you feeling right now?',
};

export const ROTATING_QUESTIONS: SurveyQuestion[] = [...SELF_EFFICACY_QUESTIONS, ...INTEREST_ENJOYMENT_QUESTIONS];

export const getRandomRotatingQuestion = (): SurveyQuestion => {
  const randomIndex = Math.floor(Math.random() * ROTATING_QUESTIONS.length);
  return ROTATING_QUESTIONS[randomIndex];
};

export const getDeterministicRotatingQuestion = (seed: string): SurveyQuestion => {
  // Simple hash function to get a consistent index from a seed string
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    const char = seed.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash;
  }
  const index = Math.abs(hash) % ROTATING_QUESTIONS.length;
  return ROTATING_QUESTIONS[index];
};

/**
 * Get survey questions for a brain break session.
 * First question rotates through validated measures, second is always emotion.
 *
 * @param userId - User ID for deterministic question selection
 * @param dateKey - Date string (YYYY-MM-DD) for daily rotation
 */
export const getSurveyQuestions = (userId?: string, dateKey?: string): SurveyQuestion[] => {
  let firstQuestion: SurveyQuestion;

  if (userId && dateKey) {
    firstQuestion = getDeterministicRotatingQuestion(`${userId}-${dateKey}`);
  } else {
    firstQuestion = getRandomRotatingQuestion();
  }

  return [firstQuestion, EMOTION_QUESTION];
};
